'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: {
    id: string;
    title: string;
    description: string;
    detailedDescription: string;
    features: string[];
    image: string;
    alt: string;
    priceRange: string;
    timeline: string;
    scope: string;
    process: string[];
    gallery: Array<{ image: string; alt: string }>;
  } | null;
}

const ServiceModal = ({ isOpen, onClose, service }: ServiceModalProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !service || !mounted) return null;

  return createPortal(
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div
        className="absolute inset-0 bg-secondary/90 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-card rounded-lg shadow-strong max-w-4xl w-full my-2 transition-all duration-200 ease-out animate-in zoom-in-95 fade-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 bg-card rounded-full shadow-soft hover:shadow-strong transition-all duration-200 z-10"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={24} className="text-foreground" />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          <div className="p-6 lg:p-8">
          <div className="relative h-56 sm:h-72 lg:h-[28rem] rounded-lg overflow-hidden mb-6">
            <AppImage
              src={service.image}
              alt={service.alt}
              className="w-full h-full object-cover"
            />
          </div>

          <h2 className="font-headline text-3xl font-bold text-foreground mb-4">
            {service.title}
          </h2>

          <p className="font-body text-foreground leading-relaxed mb-6">
            {service.detailedDescription}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="SparklesIcon" size={20} className="text-emerald-600" />
                <span className="font-body text-sm font-medium text-muted-foreground">
                  {(
                    {
                      modular: 'Our Approach',
                      luxury: 'Our Approach',
                      renovation: 'Our Approach',
                      turnkey: 'Our Approach',
                      flooring: 'Design Philosophy',
                      lighting: 'Our Approach',
                      automation: 'Our Approach',
                      consultation: 'Advisory',
                    } as Record<string, string>
                  )[service.id] || 'Aesthetic Design'}
                </span>
              </div>
              <p className="font-body text-sm md:text-base font-semibold leading-snug text-foreground">
                {(
                  {
                    modular: 'Intelligent, space-optimized living',
                    luxury: 'Curated elegance in every detail',
                    renovation: 'Thoughtful renewal of existing homes',
                    turnkey: 'Seamless homes, end to end',
                    flooring: 'Clean lines, seamless finishes',
                    lighting: 'Mood, warmth, and precision',
                    automation: 'Effortless living, intelligently designed',
                    consultation: 'Expert guidance for confident decisions',
                  } as Record<string, string>
                )[service.id] || 'Culturally-rooted interiors'}
              </p>
            </div>

            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="ClockIcon" size={20} className="text-emerald-600" />
                <span className="font-body text-sm font-medium text-muted-foreground">
                  Timeline
                </span>
              </div>
              <p className="font-body text-sm md:text-base font-semibold leading-snug text-foreground">
                {service.timeline}
              </p>
            </div>

            <div className="bg-background rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="HomeIcon" size={20} className="text-emerald-600" />
                <span className="font-body text-sm font-medium text-muted-foreground">
                  Scope
                </span>
              </div>
              <p className="font-body text-sm md:text-base font-semibold leading-snug text-foreground">
                {service.scope}
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="font-headline text-xl font-bold text-foreground mb-4">
              Key Features & Benefits
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon
                    name="CheckCircleIcon"
                    size={20}
                    className="text-emerald-600 flex-shrink-0 mt-0.5"
                  />
                  <span className="font-body text-sm text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="font-headline text-xl font-bold text-foreground mb-4">
              Our Process
            </h3>
            <div className="space-y-3">
              {service.process.map((step, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="font-body text-sm font-bold text-white">
                      {index + 1}
                    </span>
                  </div>
                  <p className="font-body text-foreground pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="font-headline text-xl font-bold text-foreground mb-4">
              Project Gallery
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {service.gallery.map((item, index) => (
                <div key={index} className="relative h-56 rounded-lg overflow-hidden">
                  <AppImage
                    src={item.image}
                    alt={item.alt}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              href="/contact#contact-form"
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-3 bg-emerald-600 text-white font-body font-semibold rounded-md hover:bg-emerald-700 transition-all duration-200 shadow-soft hover:shadow-strong text-center"
            >
              Book Consultation
            </Link>
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServiceModal;